// Features: M004-F0716
// Tests: T-M004-0212 (phase B)
//
// Stage-driven INLINE text for the customer ledger page (design M004-F0712-F0716 sections 5.1 and 5.3, phase B).
// One frozen map keyed by the Spec TransactionStage enum (core-ledger-account-gateway 1.4.0), so a stage added by a later
// Spec re-pin fails the table-driven test until it has a sentence here. The rules the map enforces:
//   * "on its way" wording exists for `sending_to_ledger` ONLY (Spec s.9, invariant I3). Every other stage says where the
//     cost IS, never where it is going;
//   * a LEDGER-HELD item is described by the Ledger's `stage` (ACCT-TX-002/013), never by Core's dispatch state;
//   * the failure stages carry Core's `next_action` VERBATIM; when Core sent none the fixed sentence NEXT_MISSING is shown
//     so a Core gap is visible rather than silent (design 2.4).
import { displayTxId } from '../../utils/transactionId';

export const SENDING_STAGE = 'sending_to_ledger';
export const FAILURE_STAGES = Object.freeze(['dispatch_failed', 'refused']);
export const nextMissingSentence = (handOffId, txnId) => `Core did not say what to do next. Contact an operator and quote ${displayTxId({ txnId, handOffId }) || 'this transaction'}.`;

const ADVICE_DETAIL = Object.freeze({
  coming: 'In Advice: coming. The session has not settled.',
  being_checked: 'In Advice: being checked.',
  deferred: 'In Advice.',
  rejected: 'In Advice: rejected, not to be billed.',
});

const quoted = (reason) => (typeof reason === 'string' && reason.trim() ? ` "${reason.trim()}"` : '');

// stage -> (ctx) => sentence. ctx = { detail, reason }.
export const STAGE_INLINE = Object.freeze({
  captured: () => 'Booked. Not priced yet.',
  priced: () => 'Priced. Not billed yet.',
  settled: () => 'The session has ended.',
  awaiting_approval: () => 'Waiting for approval in Billing.',
  returned_for_reprice: () => 'Returned for repricing. It needs approving again.',
  sending_to_ledger: () => 'Sending to the Ledger. Not confirmed yet.',
  handed_off: () => 'The Ledger has this cost.',
  advice: ({ detail }) => ADVICE_DETAIL[detail] || 'In Advice.',
  draft: () => 'On the next invoice.',
  issued: () => 'Invoiced.',
  posted: () => 'Posted to the ledger.',
  paid: () => 'Paid.',
  credited: () => 'Credited.',
  dispatch_failed: () => 'Failed to reach the Ledger. Not on an invoice.',
  refused: ({ reason }) => (quoted(reason) ? `Rejected:${quoted(reason)}. Will not be billed.` : 'Rejected. Will not be billed.'),
  voided: () => 'Cancelled. The booking was cancelled before billing.',
  billed_elsewhere: ({ reason }) => (quoted(reason) ? `Billed elsewhere:${quoted(reason)}.` : 'Billed elsewhere.'),
});

// An unknown / absent stage makes NO claim (null): the caller keeps whatever Core said, or shows nothing.
export function inlineStage({ stage, detail = '', reason = '', nextAction = '', handOffId = '', txnId = '' } = {}) {
  const sentence = STAGE_INLINE[stage];
  if (typeof sentence !== 'function') return null;
  const failure = FAILURE_STAGES.includes(stage);
  const given = typeof nextAction === 'string' && nextAction.trim() ? nextAction : '';
  return {
    stage,
    text: sentence({ detail, reason }),
    problem: failure,
    sending: stage === SENDING_STAGE,
    nextAction: given || (failure ? nextMissingSentence(handOffId, txnId) : ''),
    nextMissing: failure && !given,
  };
}
